// ===========================================================================
// FilterDrawer — panel lateral deslizable con todos los controles de filtro.
//
// Mantiene un BORRADOR local inicializado desde los filtros aplicados. "Apply"
// emite el borrador al padre y cierra; "Clear all" vacía el borrador pero no
// dispara la búsqueda hasta Apply. Refleja el estado actual al abrir (si un
// filtro vino de un click en el donut, aquí aparece preseleccionado).
//
// Catálogos: Location y PCP desde masterDataService; Network desde los KPIs
// (se pasan por props); Metal Level y Time Since son catálogos fijos.
// ===========================================================================
import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";
import { TIME_BUCKETS, METAL_LEVELS } from "../../common/constants";

const EMPTY = {
  dob: null,
  locationId: null,
  pcpName: null,
  networkName: null,
  metalLevel: null,
  timeBucket: null,
};

export default function FilterDrawer({ open, value, networks = [], onClose, onApply, onClear }) {
  const [draft, setDraft] = useState({ ...EMPTY });
  const [locations, setLocations] = useState([]);
  const [pcps, setPcps] = useState([]);

  // Sincroniza el borrador con los filtros aplicados cada vez que se abre.
  useEffect(() => {
    if (open) setDraft({ ...EMPTY, ...value });
  }, [open, value]);

  // Catálogos para los dropdowns (una sola carga).
  useEffect(() => {
    let active = true;
    Promise.all([
      getLocations({ status: 1, clinical: true }).catch(() => []),
      getPcps({ status: 1 }).catch(() => []),
    ]).then(([locs, pcpList]) => {
      if (!active) return;
      setLocations(
        (Array.isArray(locs) ? locs : [])
          .map((l) => ({ id: l.locationId, name: l.medicalCenter || l.centerKey || `Location ${l.locationId}` }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      );
      setPcps(
        (Array.isArray(pcpList) ? pcpList : [])
          .map((p) => ({ name: p.pcpFullName, label: p.title ? `${p.pcpFullName} (${p.title})` : p.pcpFullName }))
          .filter((p) => !!p.name)
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    });
    return () => {
      active = false;
    };
  }, []);

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val === "" ? null : val }));

  const dobValue = draft.dob ? dayjs(draft.dob) : null;
  const pcpValue = pcps.find((p) => p.name === draft.pcpName) || null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 380, maxWidth: "100%" } }}>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close filters">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2.5} sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
          <DatePicker
            label="Date of Birth"
            value={dobValue}
            onChange={(d) => set("dob", d?.isValid?.() ? d.format("YYYY-MM-DD") : null)}
            format="MM/DD/YYYY"
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true, onClear: () => set("dob", null) },
            }}
          />

          <TextField
            select
            size="small"
            label="Location"
            value={draft.locationId ?? ""}
            onChange={(e) => set("locationId", e.target.value === "" ? null : Number(e.target.value))}
            fullWidth
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            size="small"
            options={pcps}
            value={pcpValue}
            onChange={(_, opt) => set("pcpName", opt ? opt.name : null)}
            getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
            isOptionEqualToValue={(o, v) => o.name === v.name}
            renderInput={(params) => <TextField {...params} label="Assigned PCP" placeholder="All" />}
          />

          <TextField
            select
            size="small"
            label="Network"
            value={draft.networkName ?? ""}
            onChange={(e) => set("networkName", e.target.value)}
            fullWidth
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {networks.map((n) => (
              <MenuItem key={n.networkName} value={n.networkName}>
                {n.networkName}
              </MenuItem>
            ))}
          </TextField>

          <FormControl>
            <FormLabel sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Metal Level</FormLabel>
            <RadioGroup
              value={draft.metalLevel ?? ""}
              onChange={(e) => set("metalLevel", e.target.value)}
              sx={{ flexDirection: "row", flexWrap: "wrap" }}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label="All" />
              {METAL_LEVELS.map((m) => (
                <FormControlLabel key={m} value={m} control={<Radio size="small" />} label={m} />
              ))}
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Time Since Last Visit</FormLabel>
            <RadioGroup
              value={draft.timeBucket ?? ""}
              onChange={(e) => set("timeBucket", e.target.value)}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label="All" />
              {TIME_BUCKETS.map((b) => (
                <FormControlLabel key={b} value={b} control={<Radio size="small" />} label={b} />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDraft({ ...EMPTY });
              onClear?.();
            }}
          >
            Clear all
          </Button>
          <Button fullWidth variant="contained" onClick={() => onApply?.(draft)}>
            Apply
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
